import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ICountries } from '../../../../services/data/data-service.interface';
import { DestroySubscribers } from '../../../../decorators/destroy-subscribers.decorator';
import { ADD_ITEM_PREFIX, CountryRegion, CountryRegionForm, RegionOptionsByGroup } from './types';

@Component({
  selector: 'gtd-country-region',
  templateUrl: './country-region.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryRegionComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class CountryRegionComponent implements OnInit, ControlValueAccessor {
  @Input() set data(countries: ICountries) {
    this.countries = countries;
  }

  @Input() multiple = false;

  public countries: ICountries;

  public regionsByCountryOptions: RegionOptionsByGroup[] = [];

  public formGroup = new FormGroup<CountryRegionForm>({
    country_code: new FormControl<string[]>([]),
    region_code: new FormControl<string[]>([]),
  });

  public subscribers: Record<string, Subscription> = {};

  public options: RegionOptionsByGroup[] = [];

  public selectedRegions: RegionOptionsByGroup[] = [];

  private groups: Record<string, RegionOptionsByGroup[]> = {};

  public ngOnInit(): void {
    this.subscribers.countryCodeValueChanges = this.formGroup.controls.country_code.valueChanges.subscribe((country_codes) => {
      this.regionsByCountryOptions = [];
      (country_codes || []).forEach((code) => this.addCountryGroup(code));
      this.options = this.regionsByCountryOptions;
    });
    this.subscribers.valueChanges = this.formGroup.valueChanges.subscribe((value) => {
      const country_code = value.country_code?.length > 0 ? value.country_code : undefined;
      const region_code = value.region_code?.length > 0 ? value.region_code : undefined;
      this.onChange({ country_code, region_code });
    });
  }

  public onChange: (value: any) => void = () => {};

  public onTouch = () => {};

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public writeValue(value: CountryRegion | CountryRegion[]): void {
    if (!value) {
      this.formGroup.setValue({ country_code: [], region_code: [] });
      this.selectedRegions = [];
      return;
    }
    const countryRegion = Array.isArray(value) ? value[0] : value;
    countryRegion.country_code?.forEach((code) => this.addCountryGroup(code));
    this.regionsByCountryOptions = this.regionsByCountryOptions.concat(this.getWithoutGroupRegionsOptions(countryRegion.region_code || []));
    this.options = this.regionsByCountryOptions;
    this.selectedRegions = this.regionsByCountryOptions.filter((option) => countryRegion.region_code?.includes(option.value));
    this.formGroup.patchValue(countryRegion);
  }

  public search(search: { term: string }): void {
    if (!search.term) {
      this.options = this.regionsByCountryOptions;
      return;
    }
    if (this.regionsByCountryOptions.find((option) => search.term === option.label || search.term === option.value)) {
      return;
    }
    const addedItem = {
      label: `${ADD_ITEM_PREFIX} "${search.term}"`,
      value: search.term,
      group: '',
    };
    this.options = [addedItem, ...this.regionsByCountryOptions];
  }

  public add(item: RegionOptionsByGroup) {
    if (!item.group) {
      item.label = item.label.replace(ADD_ITEM_PREFIX, '').trim().slice(1, -1);
      this.regionsByCountryOptions = [item, ...this.regionsByCountryOptions];
      this.options = this.regionsByCountryOptions;
    } else {
      this.options = this.regionsByCountryOptions;
    }
    this.selectedRegions = this.selectedRegions.concat([item]);
  }

  public remove(item: RegionOptionsByGroup): void {
    const regionsControl = this.formGroup.controls.region_code;
    this.selectedRegions = this.selectedRegions.filter((option) => option.value !== item.value);
    if (!item.group) {
      this.options = this.options.filter((option) => option.value !== item.value);
      this.regionsByCountryOptions = this.regionsByCountryOptions.filter((option) => option.value !== item.value);
    }
    regionsControl.setValue(regionsControl.value.filter((region) => region !== item.value));
  }

  private addCountryGroup(countryCode: string): void {
    const country = this.countries[countryCode];
    if (this.groups[countryCode]) {
      this.regionsByCountryOptions = this.regionsByCountryOptions.concat(this.groups[countryCode]);
      return;
    }
    const regionsByGroup = this.regionsByGroup(country.name, country.regions);
    this.regionsByCountryOptions = this.regionsByCountryOptions.concat(regionsByGroup);
    this.groups[countryCode] = regionsByGroup;
  }

  private regionsByGroup(countryName: string, regions: Record<string, string> | undefined): RegionOptionsByGroup[] {
    return Object.entries(regions || []).map((entry) => ({ value: entry[0], label: entry[1], group: countryName }));
  }

  private getWithoutGroupRegionsOptions(regions: string[]): RegionOptionsByGroup[] {
    return regions
      .filter((code) => !this.regionsByCountryOptions.find((option) => option.value === code))
      .map((region) => ({ label: region, value: region, group: '' }));
  }
}
